import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Trash2, 
  Wand2, 
  BarChart3, 
  LineChart, 
  PieChart, 
  Table2,
  Save,
  Eye,
  ArrowLeft,
  Loader2,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = "";

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart', icon: BarChart3 },
  { value: 'line', label: 'Line Chart', icon: LineChart },
  { value: 'pie', label: 'Pie Chart', icon: PieChart },
  { value: 'stacked_bar', label: 'Stacked Bar', icon: BarChart3 },
  { value: 'combo', label: 'Combo (Bar + Line)', icon: LineChart },
  { value: 'waterfall', label: 'Waterfall', icon: BarChart3 },
  { value: 'treemap', label: 'Treemap', icon: PieChart },
  { value: 'gauge', label: 'Gauge / Meter', icon: PieChart },
  { value: 'map', label: 'World Map', icon: Globe },
  { value: 'table', label: 'Data Table', icon: Table2 },
];

const AGGREGATIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'count', label: 'Count' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

const DashboardBuilderPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  
  const [files, setFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [columnsInfo, setColumnsInfo] = useState({ numeric: [], categorical: [], date: [] });
  
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [charts, setCharts] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [originalPublished, setOriginalPublished] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (editId && files.length > 0) {
      loadDashboard(editId);
    }
  }, [editId, files]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/files`, { withCredentials: true });
      setFiles(response.data);
    } catch (e) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async (dashId) => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboards/${dashId}`, { withCredentials: true });
      const d = response.data;
      setDashboardName(d.name);
      setDashboardDescription(d.description || '');
      setCharts(d.charts || []);
      setKpis(d.kpis || []);
      setOriginalPublished(d.is_published || false);
      
      const fileId = d.file_id;
      setSelectedFileId(fileId);
      const file = files.find(f => f.id === fileId);
      if (file) setSelectedFile(file);
    } catch (e) {
      toast.error('Failed to load dashboard for editing');
      navigate('/reports');
    }
  };

  const handleFileSelect = async (fileId) => {
    setSelectedFileId(fileId);
    const file = files.find(f => f.id === fileId);
    setSelectedFile(file);
    if (!isEditMode) setDashboardName(`${file?.filename || 'Data'} Dashboard`);
    setCharts([]);
    setKpis([]);
    setColumnsInfo({ numeric: [], categorical: [], date: [] });
  };

  const handleAISuggest = async () => {
    if (!selectedFileId) { toast.error('Please select a file first'); return; }
    setAiLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/dashboards/ai-suggest`, { file_id: selectedFileId }, { withCredentials: true });
      setColumnsInfo(response.data.columns_info);
      setCharts(response.data.charts);
      setKpis(response.data.kpis);
      toast.success('AI suggestions generated!');
    } catch (e) {
      toast.error('Failed to generate suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const addChart = () => {
    setCharts([...charts, { chart_type: 'bar', title: 'New Chart', x_column: columnsInfo.categorical[0] || selectedFile?.columns?.[0] || '', y_column: columnsInfo.numeric[0] || '' }]);
  };
  const updateChart = (index, updates) => { const c = [...charts]; c[index] = { ...c[index], ...updates }; setCharts(c); };
  const removeChart = (index) => setCharts(charts.filter((_, i) => i !== index));

  const addKPI = () => {
    setKpis([...kpis, { title: 'New KPI', column: columnsInfo.numeric[0] || selectedFile?.columns?.[0] || '', aggregation: 'sum', format: 'number' }]);
  };
  const updateKPI = (index, updates) => { const k = [...kpis]; k[index] = { ...k[index], ...updates }; setKpis(k); };
  const removeKPI = (index) => setKpis(kpis.filter((_, i) => i !== index));

  const saveDashboard = async (publish = false) => {
    if (!dashboardName.trim()) { toast.error('Please enter a dashboard name'); return; }
    if (!selectedFileId) { toast.error('Please select a file'); return; }
    if (charts.length === 0 && kpis.length === 0) { toast.error('Add at least one chart or KPI'); return; }
    
    setSaving(true);
    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/api/dashboards/${editId}`, {
          name: dashboardName,
          description: dashboardDescription,
          charts, kpis,
          is_published: publish
        }, { withCredentials: true });
        toast.success('Dashboard updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/dashboards`, {
          name: dashboardName,
          description: dashboardDescription,
          file_id: selectedFileId,
          charts, kpis,
          is_published: publish
        }, { withCredentials: true });
        toast.success(`Dashboard ${publish ? 'published' : 'saved'} successfully!`);
      }
      navigate('/reports');
    } catch (e) {
      toast.error('Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-zinc-200 bg-white">
          <CardContent className="p-12 text-center">
            <h2 className="text-xl font-semibold text-zinc-950 mb-2">Admin Access Required</h2>
            <p className="text-zinc-500">Only administrators can create dashboards.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="dashboard-builder-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
              {isEditMode ? 'Edit Dashboard' : 'Dashboard Builder'}
            </h1>
            <p className="text-zinc-500 mt-1">
              {isEditMode ? 'Modify your dashboard configuration' : 'Create visualizations from your data'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => saveDashboard(false)} disabled={saving} className="border-zinc-200">
            <Save className="w-4 h-4 mr-2" />
            {isEditMode ? 'Save Changes' : 'Save Draft'}
          </Button>
          <Button onClick={() => saveDashboard(true)} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="publish-dashboard-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            {isEditMode ? 'Save & Publish' : 'Publish'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-zinc-200 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-zinc-950">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Data File</Label>
                <Select value={selectedFileId} onValueChange={handleFileSelect} disabled={isEditMode}>
                  <SelectTrigger className="border-zinc-200" data-testid="select-file-dropdown">
                    <SelectValue placeholder="Choose a file..." />
                  </SelectTrigger>
                  <SelectContent>
                    {files.map(file => (
                      <SelectItem key={file.id} value={file.id}>{file.filename} ({file.row_count} rows)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedFile && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium mb-2">Optional: Let AI analyze your data and suggest charts & KPIs</p>
                  <Button onClick={handleAISuggest} disabled={aiLoading} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white" data-testid="ai-suggest-button">
                    {aiLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing Data...</>) : (<><Wand2 className="w-4 h-4 mr-2" />AI Auto-Suggest (Optional)</>)}
                  </Button>
                  <p className="text-xs text-blue-500 mt-1.5 text-center">Or configure charts & KPIs manually below</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Dashboard Name</Label>
                <Input value={dashboardName} onChange={(e) => setDashboardName(e.target.value)} placeholder="Enter dashboard name" className="border-zinc-200" data-testid="dashboard-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea value={dashboardDescription} onChange={(e) => setDashboardDescription(e.target.value)} placeholder="Describe what this dashboard shows..." className="border-zinc-200" rows={2} />
              </div>
            </CardContent>
          </Card>

          {/* KPIs */}
          <Card className="border-zinc-200 bg-white">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-950">KPI Cards</CardTitle>
              <Button variant="outline" size="sm" onClick={addKPI} className="border-zinc-200"><Plus className="w-4 h-4 mr-1" />Add KPI</Button>
            </CardHeader>
            <CardContent>
              {kpis.length === 0 ? (
                <p className="text-zinc-400 text-center py-4">No KPIs added yet. Click "AI Auto-Suggest" or add manually.</p>
              ) : (
                <div className="space-y-3">
                  {kpis.map((kpi, index) => (
                    <div key={index} className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        <Input value={kpi.title} onChange={(e) => updateKPI(index, { title: e.target.value })} placeholder="KPI Title" className="border-zinc-200" />
                        <Select value={kpi.column} onValueChange={(v) => updateKPI(index, { column: v })}>
                          <SelectTrigger className="border-zinc-200"><SelectValue placeholder="Column" /></SelectTrigger>
                          <SelectContent>
                            {selectedFile?.columns?.map(col => (<SelectItem key={col} value={col}>{col}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <Select value={kpi.aggregation} onValueChange={(v) => updateKPI(index, { aggregation: v })}>
                          <SelectTrigger className="border-zinc-200"><SelectValue placeholder="Aggregation" /></SelectTrigger>
                          <SelectContent>
                            {AGGREGATIONS.map(agg => (<SelectItem key={agg.value} value={agg.value}>{agg.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => removeKPI(index)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      {/* Threshold Config */}
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <Input type="number" value={kpi.warning_threshold ?? ''} onChange={(e) => updateKPI(index, { warning_threshold: e.target.value ? parseFloat(e.target.value) : null })} placeholder="Warning threshold" className="border-zinc-200 text-xs" />
                        <Input type="number" value={kpi.danger_threshold ?? ''} onChange={(e) => updateKPI(index, { danger_threshold: e.target.value ? parseFloat(e.target.value) : null })} placeholder="Danger threshold" className="border-zinc-200 text-xs" />
                        <Select value={kpi.threshold_direction || 'above'} onValueChange={(v) => updateKPI(index, { threshold_direction: v })}>
                          <SelectTrigger className="border-zinc-200 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="above">Good when above</SelectItem>
                            <SelectItem value="below">Good when below</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Charts */}
          <Card className="border-zinc-200 bg-white">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-950">Charts</CardTitle>
              <Button variant="outline" size="sm" onClick={addChart} className="border-zinc-200"><Plus className="w-4 h-4 mr-1" />Add Chart</Button>
            </CardHeader>
            <CardContent>
              {charts.length === 0 ? (
                <p className="text-zinc-400 text-center py-4">No charts added yet. Click "AI Auto-Suggest" or add manually.</p>
              ) : (
                <div className="space-y-4">
                  {charts.map((chart, index) => (
                    <div key={index} className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {(() => { const ChartIcon = CHART_TYPES.find(t => t.value === chart.chart_type)?.icon || BarChart3; return <ChartIcon className="w-5 h-5 text-blue-700" />; })()}
                          <span className="font-medium text-zinc-950">{chart.title}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeChart(index)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Input value={chart.title} onChange={(e) => updateChart(index, { title: e.target.value })} className="border-zinc-200 mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">Chart Type</Label>
                          <Select value={chart.chart_type} onValueChange={(v) => updateChart(index, { chart_type: v })}>
                            <SelectTrigger className="border-zinc-200 mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CHART_TYPES.map(type => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        {chart.chart_type !== 'table' && (
                          <>
                            <div>
                              <Label className="text-xs">{chart.chart_type === 'map' ? 'Country Column' : 'X-Axis'}</Label>
                              <Select value={chart.x_column || ''} onValueChange={(v) => updateChart(index, { x_column: v })}>
                                <SelectTrigger className="border-zinc-200 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                                <SelectContent>
                                  {selectedFile?.columns?.map(col => (<SelectItem key={col} value={col}>{col}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                            {chart.chart_type !== 'pie' && chart.chart_type !== 'gauge' && (
                              <div>
                                <Label className="text-xs">{chart.chart_type === 'map' ? 'Value Column' : 'Y-Axis'}</Label>
                                <Select value={chart.y_column || ''} onValueChange={(v) => updateChart(index, { y_column: v })}>
                                  <SelectTrigger className="border-zinc-200 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                                  <SelectContent>
                                    {selectedFile?.columns?.map(col => (<SelectItem key={col} value={col}>{col}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {chart.chart_type === 'gauge' && (
                              <div>
                                <Label className="text-xs">Metric Column</Label>
                                <Select value={chart.y_column || ''} onValueChange={(v) => updateChart(index, { y_column: v })}>
                                  <SelectTrigger className="border-zinc-200 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                                  <SelectContent>
                                    {selectedFile?.columns?.map(col => (<SelectItem key={col} value={col}>{col}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {chart.chart_type === 'combo' && (
                              <div>
                                <Label className="text-xs">Y2-Axis (Line)</Label>
                                <Select value={chart.y2_column || ''} onValueChange={(v) => updateChart(index, { y2_column: v })}>
                                  <SelectTrigger className="border-zinc-200 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                                  <SelectContent>
                                    {selectedFile?.columns?.map(col => (<SelectItem key={col} value={col}>{col}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {chart.chart_type === 'stacked_bar' && (
                              <div>
                                <Label className="text-xs">Group By</Label>
                                <Select value={chart.group_column || ''} onValueChange={(v) => updateChart(index, { group_column: v })}>
                                  <SelectTrigger className="border-zinc-200 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                                  <SelectContent>
                                    {selectedFile?.columns?.map(col => (<SelectItem key={col} value={col}>{col}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel */}
        <div className="space-y-6">
          <Card className="border-zinc-200 bg-white sticky top-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-zinc-950">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditMode && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-700">Editing Mode</p>
                  <p className="text-sm text-amber-600 mt-1">Changes will update the existing dashboard</p>
                </div>
              )}
              <div className="p-4 bg-zinc-50 rounded-lg">
                <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">Selected File</p>
                <p className="text-zinc-950 font-medium">{selectedFile?.filename || 'None selected'}</p>
                {selectedFile && <p className="text-sm text-zinc-500">{selectedFile.row_count} rows &bull; {selectedFile.columns?.length} columns</p>}
              </div>
              <div className="p-4 bg-zinc-50 rounded-lg">
                <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">Dashboard Contents</p>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-zinc-600">KPI Cards</span><span className="font-semibold text-zinc-950">{kpis.length}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Charts</span><span className="font-semibold text-zinc-950">{charts.length}</span></div>
                </div>
              </div>
              {columnsInfo.numeric.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-blue-700 mb-2">Detected Columns</p>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-blue-600">Numeric:</span> {columnsInfo.numeric.join(', ') || 'None'}</p>
                    <p><span className="text-blue-600">Categorical:</span> {columnsInfo.categorical.slice(0, 3).join(', ') || 'None'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardBuilderPage;
